import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useRemediationActionResolver } from 'App/ProgressAlert/useRemediationActionResolver';
import { extractErrorRemediationDetails } from 'App/ProgressAlert/utils';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { HTML5TroubleshootPayload } from 'Workspace/ResourceProvider/Context';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

interface TroubleshootRunnerProps {
	pendingTroubleshoot: HTML5TroubleshootPayload;
	resource: Resource;
	clearPendingTroubleshoot: () => void;
}

const TroubleshootRunner = ({
	pendingTroubleshoot,
	resource,
	clearPendingTroubleshoot,
}: TroubleshootRunnerProps): JSX.Element | null => {
	const { errorId, correlationId } = pendingTroubleshoot;
	const action = extractErrorRemediationDetails(errorId)?.remediationActionType;

	const onComplete = useCallback(() => {
		clearPendingTroubleshoot();
	}, [clearPendingTroubleshoot]);

	const { remediationFunction } = useRemediationActionResolver(
		resource,
		action,
		correlationId,
		errorId,
		onComplete,
		true
	);

	const calledRef = useRef(false);

	useEffect(() => {
		calledRef.current = false;
	}, [pendingTroubleshoot]);

	useEffect(() => {
		if (calledRef.current) {
			return;
		}
		calledRef.current = true;
		if (!remediationFunction) {
			clearPendingTroubleshoot();
			return;
		}
		remediationFunction();
	}, [remediationFunction, clearPendingTroubleshoot]);

	return null;
};

export const HTML5TroubleshootBridge = (): JSX.Element | null => {
	const { value } = useLoadableResourceContext();
	const troubleshootQueue = value?.troubleshootQueue ?? [];
	const clearPendingTroubleshoot = value?.clearPendingTroubleshoot;
	const pendingTroubleshoot = troubleshootQueue[0] ?? null;
	const resources = value?.resources;
	const resource = resources?.find(r => r.id === pendingTroubleshoot?.resourceId);

	useEffect(() => {
		if (pendingTroubleshoot && resources && !resource) {
			clearPendingTroubleshoot?.();
		}
	}, [pendingTroubleshoot, resources, resource, clearPendingTroubleshoot]);

	if (!pendingTroubleshoot || !resource || !clearPendingTroubleshoot) {
		return null;
	}

	return (
		<TroubleshootRunner
			pendingTroubleshoot={pendingTroubleshoot}
			resource={resource}
			clearPendingTroubleshoot={clearPendingTroubleshoot}
		/>
	);
};
